import { useEffect, useId, useRef, useState } from 'react'

/**
 * Shared menu-button machinery: open state, a container ref for click-outside
 * detection, a toggle ref for focus restoration, an item-ref registry, and
 * Escape / click-outside / Arrow-Home-End keyboard handling with wraparound.
 * ReportToolbar's add-filter menu implements the ARIA menu-button pattern
 * with it; factored out as its own hook so a second consumer can reuse it
 * rather than duplicating it.
 *
 * Deliberately does NOT decide what happens when an item is chosen: that's
 * why `close` takes a `restoreFocus` option rather than always focusing the
 * toggle. ReportToolbar returns focus to the toggle on Escape/outside-click,
 * but moves focus into the newly-revealed chip's select on item-selection
 * (choosing a filter kind starts a new interaction) -- that difference stays
 * a caller decision, made by passing `{ restoreFocus: false }` to `close`
 * and taking over focus placement itself.
 */
const useMenu = () => {
    const [isOpen, setIsOpen] = useState(false)
    const containerRef = useRef(null)
    const toggleRef = useRef(null)
    const itemRefs = useRef(new Map())
    const menuId = useId()

    const open = () => setIsOpen(true)

    const close = ({ restoreFocus = true } = {}) => {
        setIsOpen(false)
        if (restoreFocus) toggleRef.current?.focus()
    }

    // Escape and click-outside both close the menu without picking anything.
    useEffect(() => {
        if (!isOpen) return undefined

        const onDocumentClick = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                // Stop the browser's own mousedown-triggered focus shift so the
                // focus restoration in close() is the only thing that moves focus.
                // Without this, clicking a focusable outside element (or even an
                // unfocusable one, which drops focus to <body>) wins the race
                // against toggleRef.current.focus() because the browser assigns
                // focus on mousedown, before React's effects run. This only fires
                // when the click is actually outside the container, so a click on
                // a menu item or the toggle itself is never affected.
                event.preventDefault()
                close()
            }
        }
        const onKeyDown = (event) => {
            if (event.key === 'Escape') close()
        }

        document.addEventListener('mousedown', onDocumentClick)
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('mousedown', onDocumentClick)
            document.removeEventListener('keydown', onKeyDown)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    // A ref-callback factory for menu items: `ref={registerItem(key)}` keeps
    // the item-ref map (used for focus navigation) in sync as items mount
    // and unmount, without every caller re-deriving the same set/delete pair.
    const registerItem = (key) => (node) => {
        if (node) itemRefs.current.set(key, node)
        else itemRefs.current.delete(key)
    }

    // `keys` is the caller's current ordered list of item keys (it changes as
    // items are filtered or added/removed), so wraparound always reflects
    // what's actually navigable right now rather than a stale snapshot.
    const focusItemAt = (keys, index) => {
        if (keys.length === 0) return
        const wrapped = ((index % keys.length) + keys.length) % keys.length
        itemRefs.current.get(keys[wrapped])?.focus()
    }

    const onItemKeyDown = (keys, key) => (event) => {
        const index = keys.findIndex((itemKey) => itemKey === key)
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            focusItemAt(keys, index + 1)
        } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            focusItemAt(keys, index - 1)
        } else if (event.key === 'Home') {
            event.preventDefault()
            focusItemAt(keys, 0)
        } else if (event.key === 'End') {
            event.preventDefault()
            focusItemAt(keys, keys.length - 1)
        }
    }

    return {
        isOpen,
        open,
        close,
        containerRef,
        toggleRef,
        menuId,
        registerItem,
        focusItemAt,
        onItemKeyDown,
    }
}

export default useMenu
